import React, { useState } from 'react';
import { FiCheckCircle, FiClock, FiXCircle, FiAlertCircle, FiLock } from 'react-icons/fi';
import './SignatureStatus.scss';
import { useTranslation } from 'react-i18next';

/**
 * Componente que muestra el estado de firma de un documento de Signaturit
 * Muestra un badge con el estado y al hacer hover muestra el detalle de firmantes
 * 
 * Props:
 * - signatureData: Objeto con la información de firma del documento
 *   {
 *     last_event: string - Ver SIGNATURIT_STATUS para todos los posibles valores:
 *       IN_PROGRESS: PROCESSING, email_processed, email_delivered, reminder_email_processed, 
 *                    reminder_email_delivered, sms_processed, sms_delivered, document_opened
 *       SIGNING: document_signed
 *       COMPLETED: document_completed, audit_trail_completed
 *       FAILED: email_bounced, email_deferred, document_declined, document_expired, document_canceled, error
 *     error: string - Descripción del error cuando last_event es "error"
 *     expiration_date: string (formato ISO)
 *     recipients: [
 *       { email: string, full_name: string, status: string (SIGNED, PENDING, DECLINED, REJECTED) }
 *     ]
 *   }
 * - onStartSign: Función callback para iniciar el proceso de firma (opcional)
 * - canSign: Boolean que indica si el documento es signable (opcional, por defecto true)
 * - hasSubscriptionAccess: Boolean que indica si el usuario tiene acceso a la funcionalidad de firmar documentos (opcional, por defecto true)
 */
const SignatureStatus = ({ signatureData, onStartSign, canSign = true, hasSubscriptionAccess = true }) => {
    const { t } = useTranslation();
    const [showTooltip, setShowTooltip] = useState(false);

    if (!signatureData) {
        if (onStartSign && canSign) {
            const isSubscriptionBlocked = !hasSubscriptionAccess;

            return (
                <button
                    className={`btn-start-signature ${isSubscriptionBlocked ? 'disabled-subscription' : ''}`}
                    onClick={(e) => {
                        e.preventDefault();
                        onStartSign();
                    }}
                    title={t("documents.startSignatureProcess")}
                    style={{
                        opacity: isSubscriptionBlocked ? 0.5 : 1,
                        cursor: isSubscriptionBlocked ? 'not-allowed' : 'pointer',
                        position: 'relative',
                    }}
                >
                    {t("documents.startSignature")}
                    {isSubscriptionBlocked && (
                        <FiLock
                            style={{
                                fontSize: '0.7em',
                                marginLeft: '5px',
                                position: 'absolute',
                                top: '2px',
                                right: '5px',
                            }}
                        />
                    )}
                </button>
            );
        }
        return null;
    }

    const { last_event, expiration_date, recipients, error } = signatureData;

    const getStatusConfig = () => {
        const signedCount = recipients ? recipients.filter(r => r.status === 'SIGNED').length : 0;
        const totalCount = recipients ? recipients.length : 0;

        const IN_PROGRESS = [
            'PROCESSING',
            'email_processed',
            'email_delivered',
            'reminder_email_processed',
            'reminder_email_delivered',
            'sms_processed',
            'sms_delivered',
            'document_opened'
        ];

        const SIGNING = ['document_signed'];

        const COMPLETED = [
            'document_completed',
            'audit_trail_completed'
        ];

        const FAILED = [
            'email_bounced',
            'email_deferred',
            'document_declined',
            'document_expired',
            'document_canceled',
            'error'
        ];

        if (COMPLETED.includes(last_event)) {
            return {
                label: t("documents.signature.completed"),
                count: `(${totalCount}/${totalCount})`,
                showCount: true,
                className: 'status-completed',
                icon: <FiCheckCircle />,
                detailText: t("documents.signature.completedOn", { date: formatDate(expiration_date) })
            };
        }

        if (SIGNING.includes(last_event)) {
            return {
                label: t("documents.signature.inProgress"),
                count: `(${signedCount}/${totalCount})`,
                showCount: true,
                className: 'status-in-progress',
                icon: <FiClock />,
                detailText: t("documents.signature.expiresOn", { date: formatDate(expiration_date) })
            };
        }

        if (IN_PROGRESS.includes(last_event)) {
            return {
                label: t("documents.signature.inProgress"),
                count: `(${signedCount}/${totalCount})`,
                showCount: true,
                className: 'status-in-progress',
                icon: <FiClock />,
                detailText: t("documents.signature.expiresOn", { date: formatDate(expiration_date) })
            };
        }

        if (FAILED.includes(last_event)) {
            if (last_event === 'error') {
                return {
                    label: t("documents.signature.error"),
                    className: 'status-error',
                    icon: <FiAlertCircle />,
                    detailText: error || t("documents.signature.genericError")
                };
            } else if (last_event === 'document_expired') {
                return {
                    label: t("documents.signature.expired"),
                    className: 'status-expired',
                    icon: <FiXCircle />,
                    detailText: t("documents.signature.expiredOn", { date: formatDate(expiration_date) })
                };
            } else if (last_event === 'document_declined') {
                return {
                    label: t("documents.signature.declined"),
                    className: 'status-declined',
                    icon: <FiXCircle />,
                    detailText: expiration_date
                        ? t("documents.signature.declinedOn", { date: formatDate(expiration_date) })
                        : t("documents.signature.documentDeclined")
                };
            } else if (last_event === 'document_canceled') {
                return {
                    label: t("documents.signature.cancelled"),
                    className: 'status-declined',
                    icon: <FiXCircle />,
                    detailText: t("documents.signature.documentCancelled")
                };
            } else if (last_event === 'email_bounced' || last_event === 'email_deferred') {
                return {
                    label: t("documents.signature.sendError"),
                    className: 'status-error',
                    icon: <FiAlertCircle />,
                    detailText: t("documents.signature.emailError")
                };
            }
        }

        return {
            label: t("documents.signature.processing"),
            className: 'status-processing',
            icon: <FiClock />,
            detailText: expiration_date
                ? t("documents.signature.expiresOn", { date: formatDate(expiration_date) })
                : t("documents.signature.processing")
        };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const statusConfig = getStatusConfig();

    if (!statusConfig) {
        return null;
    }

    return (
        <div 
            className="signature-status-wrapper"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <div className={`signature-status-badge ${statusConfig.className}`}>
                <span className="status-icon">{statusConfig.icon}</span>
                <span className="status-label">{statusConfig.label}</span>
                {statusConfig.showCount && (
                    <span className="status-count">{statusConfig.count}</span>
                )}
            </div>
            
            {showTooltip && (last_event === 'error' || (recipients && recipients.length > 0)) && (
                <div className="signature-status-tooltip">
                    <div className="tooltip-header">
                        {statusConfig.detailText}
                    </div>
                    {recipients && recipients.length > 0 && <div className="tooltip-recipients">
                        {recipients.map((recipient, index) => {
                            let statusClass = 'pending';
                            let statusIcon = <FiClock />;
                            
                            if (recipient.status === 'SIGNED') {
                                statusClass = 'signed';
                                statusIcon = <FiCheckCircle />;
                            } else if (recipient.status === 'DECLINED' || recipient.status === 'REJECTED') {
                                statusClass = 'declined';
                                statusIcon = <FiXCircle />;
                            }
                            
                            return (
                                <div key={index} className="recipient-item">
                                    <span className={`recipient-status ${statusClass}`}>
                                        {statusIcon}
                                    </span>
                                    <span className="recipient-name">{recipient.full_name}</span>
                                </div>
                            );
                        })}
                    </div>}
                </div>
            )}
        </div>
    );
};

export default SignatureStatus;

